import { refreshIcons, setIcon } from './icons';

const API_ENDPOINT = '/api';
const MAX_PHOTOS = 20;
const MAX_IMAGE_DIMENSION = 1920;
const TARGET_JPEG_QUALITY = 0.82;

interface Photo {
  id: number;
  user_id: number;
  file_path: string;
  grid_position: number;
  section?: 'feed' | 'draft';
  created_at: string;
}

interface User {
  id: number;
  username: string;
}

interface AppState {
  photos: Photo[];
  feedPhotos: Photo[];
  draftPhotos: Photo[];
  section: 'feed' | 'draft';
  user: User | null;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  authenticated?: boolean;
  user?: User;
}

interface AuthPayload {
  username: string;
  password: string;
}

interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

document.addEventListener('DOMContentLoaded', () => {
  const grid = document.getElementById('grid-container') as HTMLElement;
  const uploadBtn = document.getElementById('upload-btn') as HTMLButtonElement;
  const resetBtn = document.getElementById('reset-btn') as HTMLButtonElement;
  const fileInput = document.getElementById('file-input') as HTMLInputElement;
  const previewBtn = document.getElementById('preview-btn') as HTMLButtonElement;
  const previewModal = document.getElementById('preview-modal') as HTMLElement;
  const previewGrid = document.getElementById('preview-grid') as HTMLElement;
  const closePreviewBtn = document.getElementById('close-preview-btn') as HTMLButtonElement;
  const slotCounter = document.getElementById('slot-counter') as HTMLElement;
  const uploadProgress = document.getElementById('upload-progress') as HTMLElement;
  const uploadProgressBar = document.getElementById('upload-progress-bar') as HTMLElement;
  const uploadProgressText = document.getElementById('upload-progress-text') as HTMLElement;
  const notifier = document.getElementById('notifier') as HTMLElement;
  const authPanel = document.getElementById('auth-panel') as HTMLElement;
  const plannerArea = document.getElementById('planner-area') as HTMLElement;
  const loginForm = document.getElementById('login-form') as HTMLFormElement;
  const registerForm = document.getElementById('register-form') as HTMLFormElement;
  const authTabLogin = document.getElementById('auth-tab-login') as HTMLButtonElement;
  const authTabRegister = document.getElementById('auth-tab-register') as HTMLButtonElement;
  const logoutBtn = document.getElementById('logout-btn') as HTMLButtonElement;
  const userDisplay = document.getElementById('user-display') as HTMLElement;
  const userIdDisplay = document.getElementById('user-id-display') as HTMLElement;
  const createPostBtn = document.getElementById('create-post-btn') as HTMLButtonElement;
  const postSelectModal = document.getElementById('post-select-modal') as HTMLElement;
  const postSelectGrid = document.getElementById('post-select-grid') as HTMLElement;
  const postSelectCounter = document.getElementById('post-select-counter') as HTMLElement;
  const closePostSelectBtn = document.getElementById('close-post-select-btn') as HTMLButtonElement;
  const cancelPostBtn = document.getElementById('cancel-post-btn') as HTMLButtonElement;
  const previewPostBtn = document.getElementById('preview-post-btn') as HTMLButtonElement;
  const postPreviewModal = document.getElementById('post-preview-modal') as HTMLElement;
  const postPreviewGrid = document.getElementById('post-preview-grid') as HTMLElement;
  const closePostPreviewBtn = document.getElementById('close-post-preview-btn') as HTMLButtonElement;
  const changePasswordBtn = document.getElementById('change-password-btn') as HTMLButtonElement;
  const changePasswordModal = document.getElementById('change-password-modal') as HTMLElement;
  const changePasswordForm = document.getElementById('change-password-form') as HTMLFormElement;
  const closeChangePasswordBtn = document.getElementById('close-change-password-btn') as HTMLButtonElement;
  const cancelChangePasswordBtn = document.getElementById('cancel-change-password-btn') as HTMLButtonElement;

  const tabFeed = document.getElementById('tab-feed') as HTMLButtonElement;
  const tabDrafts = document.getElementById('tab-drafts') as HTMLButtonElement;
  const uploadFab = document.getElementById('upload-fab') as HTMLButtonElement;
  const bottomNav = document.getElementById('bottom-nav') as HTMLElement;
  const menuPanel = document.getElementById('menu-panel') as HTMLElement;
  const menuBtn = document.getElementById('menu-btn') as HTMLButtonElement;
  const closeMenuBtn = document.getElementById('close-menu-btn') as HTMLButtonElement;
  const sectionTitle = document.getElementById('section-title') as HTMLElement;
  const sectionDescription = document.getElementById('section-description') as HTMLElement;
  const themeToggleBtn = document.getElementById('theme-toggle-btn') as HTMLButtonElement;
  const themeToggleText = document.getElementById('theme-toggle-text') as HTMLElement;
  const installPrompt = document.getElementById('install-prompt') as HTMLElement;
  const installAcceptBtn = document.getElementById('install-accept-btn') as HTMLButtonElement;
  const installDismissBtn = document.getElementById('install-dismiss-btn') as HTMLButtonElement;

  const state: AppState = {
    photos: [],
    feedPhotos: [],
    draftPhotos: [],
    section: 'feed',
    user: null,
  };

  // Инициализация видимости элементов при загрузке
  // Сначала скрываем всё, потом покажем нужное после проверки авторизации
  // Это предотвращает мигание контента при загрузке
  if (plannerArea) {
    plannerArea.hidden = true;
  }
  if (bottomNav) {
    bottomNav.hidden = true;
  }
  if (uploadFab && uploadFab.parentElement) {
    uploadFab.parentElement.style.display = 'none'; // Скрываем FAB по умолчанию
  }
  if (authPanel) {
    authPanel.hidden = false; // Показываем панель авторизации по умолчанию
  }
  if (registerForm) {
    registerForm.hidden = true; // Форма регистрации скрыта по умолчанию
  }

  // ============================================
  // Simple Drag and Drop System
  // ============================================
  
  let dragState = {
    isDragging: false,
    draggedElement: null as HTMLElement | null,
    placeholder: null as HTMLElement | null,
    startX: 0,
    startY: 0,
    currentX: 0,
    currentY: 0,
    offsetX: 0,
    offsetY: 0,
  };

  let activePointerSession: {
    move: (event: PointerEvent) => void;
    up: (event: PointerEvent) => void;
  } | null = null;

  const endPointerSession = (): void => {
    if (!activePointerSession) return;
    document.removeEventListener('pointermove', activePointerSession.move);
    document.removeEventListener('pointerup', activePointerSession.up);
    document.removeEventListener('pointercancel', activePointerSession.up);
    activePointerSession = null;
  };

  const cleanupDragArtifacts = (): void => {
    if (grid) {
      grid.classList.remove('drag-active');
      grid.querySelectorAll('.grid-item--placeholder').forEach((node) => node.remove());
    }
  };

  const createPlaceholder = (): HTMLElement => {
    const placeholder = document.createElement('div');
    placeholder.className = 'grid-item grid-item--placeholder';
    return placeholder;
  };

  const startDrag = (element: HTMLElement, clientX: number, clientY: number): void => {
    if (dragState.isDragging) return;

    const rect = element.getBoundingClientRect();
    
    dragState.isDragging = true;
    dragState.draggedElement = element;
    dragState.startX = clientX;
    dragState.startY = clientY;
    dragState.currentX = clientX;
    dragState.currentY = clientY;
    dragState.offsetX = clientX - rect.left;
    dragState.offsetY = clientY - rect.top;
    
    // Create placeholder
    dragState.placeholder = createPlaceholder();
    element.parentNode?.insertBefore(dragState.placeholder, element);

    // Move to body so position:fixed uses viewport coords
    // (backdrop-filter on glass panels creates a new containing block)
    document.body.appendChild(element);
    
    // Style dragged element
    element.classList.add('dragging');
    element.style.position = 'fixed';
    element.style.zIndex = '1000';
    element.style.width = `${rect.width}px`;
    element.style.height = `${rect.height}px`;
    element.style.left = `${rect.left}px`;
    element.style.top = `${rect.top}px`;
    element.style.margin = '0';
    element.style.pointerEvents = 'none';
    element.style.transition = 'none';
    
    if (grid) grid.classList.add('drag-active');
  };

  const updateDrag = (clientX: number, clientY: number): void => {
    if (!dragState.isDragging || !dragState.draggedElement) return;
    
    dragState.currentX = clientX;
    dragState.currentY = clientY;
    
    // Update dragged element position
    const x = clientX - dragState.offsetX;
    const y = clientY - dragState.offsetY;
    dragState.draggedElement.style.left = `${x}px`;
    dragState.draggedElement.style.top = `${y}px`;
    
    // Find element under cursor
    const elementUnder = document.elementFromPoint(clientX, clientY);
    const targetItem = elementUnder?.closest('.grid-item:not(.dragging):not(.grid-item--placeholder)') as HTMLElement;
    
    if (targetItem && dragState.placeholder) {
      const targetRect = targetItem.getBoundingClientRect();
      const targetCenterY = targetRect.top + targetRect.height / 2;
      const targetCenterX = targetRect.left + targetRect.width / 2;
      
      // Determine insert position
      let insertBefore = false;
      if (clientY < targetCenterY) {
        insertBefore = true;
      } else if (clientY > targetCenterY) {
        insertBefore = false;
      } else {
        insertBefore = clientX < targetCenterX;
      }
      
      // Move placeholder
      if (insertBefore) {
        targetItem.parentNode?.insertBefore(dragState.placeholder, targetItem);
      } else {
        const nextSibling = targetItem.nextSibling;
        if (nextSibling) {
          targetItem.parentNode?.insertBefore(dragState.placeholder, nextSibling);
        } else {
          targetItem.parentNode?.appendChild(dragState.placeholder);
        }
      }
    }
  };

  const endDrag = (): void => {
    if (!dragState.isDragging || !dragState.draggedElement) {
      endPointerSession();
      cleanupDragArtifacts();
      return;
    }
    
    const element = dragState.draggedElement;
    const placeholder = dragState.placeholder;
    
    // Remove dragging styles
    element.classList.remove('dragging');
    element.style.position = '';
    element.style.zIndex = '';
    element.style.width = '';
    element.style.height = '';
    element.style.left = '';
    element.style.top = '';
    element.style.margin = '';
    element.style.pointerEvents = '';
    element.style.transition = '';
    
    // Move element back into the grid
    if (placeholder?.parentNode) {
      placeholder.parentNode.insertBefore(element, placeholder);
      placeholder.remove();
    } else if (grid && element.parentNode === document.body) {
      grid.appendChild(element);
    }

    cleanupDragArtifacts();
    
    // Reset state
    dragState.isDragging = false;
    dragState.draggedElement = null;
    dragState.placeholder = null;

    endPointerSession();
    
    // Save order
    persistOrder();
  };
  
  let notifierTimer: ReturnType<typeof setTimeout> | null = null;
  let selectedPostPhotos: Photo[] = [];

  const notify = (message: string, type: 'info' | 'error' = 'info'): void => {
    if (!notifier) return;
    notifier.textContent = message;
    notifier.dataset.type = type;
    notifier.hidden = false;

    if (notifierTimer) {
      clearTimeout(notifierTimer);
    }

    notifierTimer = setTimeout(() => {
      notifier.hidden = true;
    }, 4000);
  };

  const updateCounter = (): void => {
    if (slotCounter) {
      slotCounter.textContent = `${state.photos.length} / ${MAX_PHOTOS}`;
    }
  };

  const switchSection = (section: 'feed' | 'draft'): void => {
    state.section = section;
    state.photos = section === 'feed' ? state.feedPhotos : state.draftPhotos;

    if (tabFeed && tabDrafts) {
      tabFeed.classList.toggle('active', section === 'feed');
      tabDrafts.classList.toggle('active', section === 'draft');
    }

    const emptyStateTitle = document.querySelector('.empty-state__title') as HTMLElement;
    const emptyStateDesc = document.querySelector('.empty-state__description') as HTMLElement;

    if (section === 'feed') {
      if (sectionTitle) sectionTitle.textContent = 'Медиа';
      if (sectionDescription) sectionDescription.textContent = 'Добавьте изображения (JPG, PNG, GIF, WebP) и выстройте сетку как в Instagram.';
      if (emptyStateTitle) emptyStateTitle.textContent = 'Начните с загрузки';
      if (emptyStateDesc) emptyStateDesc.textContent = 'Загрузите до 20 изображений и выстройте идеальную сетку для вашего профиля.';
    } else {
      if (sectionTitle) sectionTitle.textContent = 'Черновики';
      if (sectionDescription) sectionDescription.textContent = 'Загрузите фото для конкретного поста, чтобы проверить их сочетаемость, не меняя основную ленту.';
      if (emptyStateTitle) emptyStateTitle.textContent = 'Нет черновиков';
      if (emptyStateDesc) emptyStateDesc.textContent = 'Загружайте фото, экспериментируйте с порядком. Это не повлияет на основную ленту.';
    }

    renderGrid();
  };

  const toggleEmptyState = (): void => {
    const isEmpty = state.photos.length === 0;
    if (grid) {
      grid.dataset.empty = isEmpty ? 'true' : 'false';
    }
    // empty-state теперь внутри grid-container, его видимость управляется через CSS
  };

  const renderGrid = (): void => {
    if (!grid) return;
    
    // Сохраняем empty-state перед очисткой
    const emptyStateElement = grid.querySelector('.empty-state');
    grid.textContent = '';
    // Восстанавливаем empty-state если он был
    if (emptyStateElement) {
      grid.appendChild(emptyStateElement);
    }
    
    state.photos.forEach((photo) => {
      const item = createGridItem(photo);
      if (item) grid.appendChild(item);
    });
    
    updateCounter();
    toggleEmptyState();
  };

  const createGridItem = (photo: Photo): HTMLElement | null => {
    const card = document.createElement('article');
    card.className = 'grid-item';
    card.dataset.id = String(photo.id);
    card.dataset.path = photo.file_path;
    card.setAttribute('role', 'listitem');
    
    // Prevent context menu and text selection
    card.style.userSelect = 'none';
    card.style.webkitUserSelect = 'none';
    (card.style as any).webkitTouchCallout = 'none';
    card.addEventListener('contextmenu', (e) => e.preventDefault());

    const img = document.createElement('img');
    img.src = photo.file_path;
    img.alt = 'Загруженное изображение';
    img.loading = 'lazy';
    img.draggable = false;
    img.style.userSelect = 'none';
    img.style.webkitUserSelect = 'none';
    (img.style as any).webkitTouchCallout = 'none';
    img.style.pointerEvents = 'none';
    img.addEventListener('contextmenu', (e) => e.preventDefault());
    
    const deleteBtn = document.createElement('button');
    deleteBtn.className = 'grid-item__delete';
    deleteBtn.type = 'button';
    deleteBtn.setAttribute('aria-label', 'Удалить фотографию');
    const deleteIcon = document.createElement('i');
    deleteIcon.setAttribute('data-lucide', 'trash-2');
    deleteIcon.setAttribute('aria-hidden', 'true');
    deleteBtn.appendChild(deleteIcon);
    refreshIcons(deleteBtn);
    deleteBtn.addEventListener('click', (event) => {
      event.stopPropagation();
      handleDeletePhoto(photo.id);
    });
    
    // Drag and drop handlers
    card.addEventListener('pointerdown', (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.grid-item__delete')) return;
      if (dragState.isDragging) return;
      
      e.preventDefault();
      
      const pointerId = e.pointerId;
      let pointerDownTime = Date.now();
      let pointerDownX = e.clientX;
      let pointerDownY = e.clientY;
      let hasMoved = false;
      
      const moveHandler = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;

        const deltaX = moveEvent.clientX - pointerDownX;
        const deltaY = moveEvent.clientY - pointerDownY;
        const distance = Math.hypot(deltaX, deltaY);
        
        if (distance > 8 && !dragState.isDragging) {
          hasMoved = true;
          card.classList.remove('show-delete');
          startDrag(card, moveEvent.clientX, moveEvent.clientY);
        }
        
        if (dragState.isDragging) {
          updateDrag(moveEvent.clientX, moveEvent.clientY);
        }
      };
      
      const upHandler = (upEvent: PointerEvent) => {
        if (upEvent.pointerId !== pointerId) return;

        if (dragState.isDragging) {
          endDrag();
        } else {
          endPointerSession();
          if (!hasMoved) {
            const duration = Date.now() - pointerDownTime;
            if (duration < 300) {
              if (grid) {
                const allCards = grid.querySelectorAll('.grid-item');
                allCards.forEach(otherCard => {
                  if (otherCard !== card) {
                    otherCard.classList.remove('show-delete');
                  }
                });
              }
              card.classList.toggle('show-delete');
            }
          }
        }
        
        try {
          if (card.hasPointerCapture(pointerId)) {
            card.releasePointerCapture(pointerId);
          }
        } catch {
          // Element may have been reparented during drag
        }
      };

      endPointerSession();
      activePointerSession = { move: moveHandler, up: upHandler };
      document.addEventListener('pointermove', moveHandler);
      document.addEventListener('pointerup', upHandler);
      document.addEventListener('pointercancel', upHandler);

      try {
        card.setPointerCapture(pointerId);
      } catch {
        // Ignore if capture fails
      }
    });

    card.appendChild(img);
    card.appendChild(deleteBtn);
    return card;
  };



  const persistOrder = async (): Promise<void> => {
    if (!grid) return;
    const order = [...grid.children]
      .map((item, index) => {
        const id = Number((item as HTMLElement).dataset.id);
        return { id, position: index };
      })
      .filter((item) => !isNaN(item.id) && item.id > 0);

    if (!order.length) {
      return;
    }

    try {
      await apiRequest('save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(order),
      });

      const photoMap = new Map(state.photos.map((photo) => [photo.id, photo]));
      const newOrder = order
        .map(({ id }) => photoMap.get(id))
        .filter((photo): photo is Photo => Boolean(photo));

      if (state.section === 'feed') {
        state.feedPhotos = newOrder;
      } else {
        state.draftPhotos = newOrder;
      }
      state.photos = newOrder;

      notify('Порядок сохранён');
    } catch (error) {
      notify((error as Error).message, 'error');
    }
  };

  const loadPhotos = async (): Promise<void> => {
    if (!state.user) {
      state.photos = [];
      state.feedPhotos = [];
      state.draftPhotos = [];
      renderGrid();
      return;
    }
    try {
      const response = await apiRequest<Photo[]>('load');
      const data = response.data || [];
      state.feedPhotos = data.filter((p) => (p.section || 'feed') === 'feed');
      state.draftPhotos = data.filter((p) => (p.section || 'feed') === 'draft');

      switchSection(state.section);
    } catch (error) {
      notify((error as Error).message, 'error');
    }
  };

  const showUploadProgress = (indeterminate = false, label: string | null = null): void => {
    if (!uploadProgress) return;
    uploadProgress.setAttribute('aria-hidden', 'false');
    uploadProgress.classList.toggle('indeterminate', indeterminate);
    uploadProgress.classList.toggle('is-over-half', false);
    if (indeterminate) {
      if (uploadProgressText) {
        uploadProgressText.textContent = label || 'Загрузка...';
      }
    } else {
      updateUploadProgress(0);
    }
  };

  const updateUploadProgress = (percent: number): void => {
    if (!uploadProgress || !uploadProgressBar || !uploadProgressText) return;
    const safePercent = Math.min(100, Math.max(0, percent));
    uploadProgressBar.style.width = `${safePercent}%`;
    uploadProgressText.textContent = `${safePercent.toFixed(0)}%`;
    uploadProgress.classList.toggle('is-over-half', safePercent >= 50);
  };

  const hideUploadProgress = (): void => {
    if (!uploadProgress) return;
    uploadProgress.setAttribute('aria-hidden', 'true');
    uploadProgress.classList.remove('indeterminate');
    uploadProgress.classList.remove('is-over-half');
    if (uploadProgressBar) {
      uploadProgressBar.style.width = '0%';
    }
    if (uploadProgressText) {
      uploadProgressText.textContent = '0%';
    }
  };

  const parseJsonSafe = <T>(text: string): T | null => {
    try {
      return text ? (JSON.parse(text) as T) : null;
    } catch (e) {
      return null;
    }
  };

  const uploadPhotos = (formData: FormData): Promise<ApiResponse<Photo[]>> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_ENDPOINT}?action=upload`);
      xhr.responseType = 'json';

      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (!uploadProgress) return;
        if (event.lengthComputable) {
          showUploadProgress(false);
          const percent = (event.loaded / event.total) * 100;
          updateUploadProgress(percent);
        } else {
          showUploadProgress(true);
        }
      };

      xhr.onload = () => {
        const response = (xhr.response ?? parseJsonSafe<ApiResponse<Photo[]>>(xhr.responseText)) as ApiResponse<Photo[]>;

        if (xhr.status === 401) {
          setAuthState(null);
          reject(new Error(response?.error || 'Требуется авторизация.'));
          return;
        }

        if (xhr.status >= 200 && xhr.status < 300 && response?.success !== false) {
          resolve(response);
        } else {
          reject(new Error(response?.error || 'Ошибка загрузки файлов.'));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Не удалось подключиться к серверу.'));
      };

      xhr.send(formData);
    });
  };

  const handleFileUpload = async (event: Event): Promise<void> => {
    if (!state.user) {
      notify('Авторизуйтесь, чтобы загружать изображения.', 'error');
      if (fileInput) fileInput.value = '';
      return;
    }

    const target = event.target as HTMLInputElement;
    const files = [...(target.files || [])];
    if (!files.length) {
      return;
    }

    if (state.photos.length + files.length > MAX_PHOTOS) {
      notify(`Можно загрузить максимум ${MAX_PHOTOS} фотографий.`, 'error');
      if (fileInput) fileInput.value = '';
      return;
    }

    showUploadProgress(true, 'Подготовка...');
    let processedFiles: File[];
    try {
      processedFiles = await preprocessFiles(files);
    } catch (error) {
      console.error('Image preprocessing failed', error);
      notify('Не удалось подготовить изображения к загрузке.', 'error');
      hideUploadProgress();
      return;
    }

    const formData = new FormData();
    processedFiles.forEach((file) => formData.append('photos[]', file));
    formData.append('section', state.section);

    showUploadProgress(true);
    try {
      const response = await uploadPhotos(formData);
      const data = response.data || [];

      if (state.section === 'feed') {
        state.feedPhotos = [...data, ...state.feedPhotos];
      } else {
        state.draftPhotos = [...state.draftPhotos, ...data];
      }
      switchSection(state.section);

      notify('Фотографии загружены');
    } catch (error) {
      notify((error as Error).message, 'error');
    } finally {
      if (fileInput) fileInput.value = '';
      hideUploadProgress();
    }
  };

  const handleDeletePhoto = async (photoId: number): Promise<void> => {
    if (!state.user) {
      notify('Авторизуйтесь, чтобы удалять фотографии.', 'error');
      return;
    }

    const shouldDelete = confirm('Удалить эту фотографию?');
    if (!shouldDelete) return;

    try {
      await apiRequest('delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: photoId }),
      });

      if (state.section === 'feed') {
        state.feedPhotos = state.feedPhotos.filter((photo) => photo.id !== photoId);
      } else {
        state.draftPhotos = state.draftPhotos.filter((photo) => photo.id !== photoId);
      }
      switchSection(state.section);

      notify('Фотография удалена');
    } catch (error) {
      notify((error as Error).message, 'error');
    }
  };

  const handleReset = async (): Promise<void> => {
    if (!state.user) {
      notify('Авторизуйтесь, чтобы очищать ленту.', 'error');
      return;
    }
    const msg =
      state.section === 'feed'
        ? 'Сбросить ленту? Будут удалены все изображения ленты.'
        : 'Очистить черновики? Будут удалены все фото из этого раздела.';

    const shouldReset = confirm(msg);
    if (!shouldReset) return;

    try {
      await apiRequest('reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ section: state.section }),
      });

      if (state.section === 'feed') {
        state.feedPhotos = [];
      } else {
        state.draftPhotos = [];
      }
      switchSection(state.section);

      notify(state.section === 'feed' ? 'Лента очищена' : 'Черновики очищены');
      closeAllModals();
    } catch (error) {
      notify((error as Error).message, 'error');
    }
  };

  const apiRequest = async <T = unknown>(action: string, options: RequestInit = {}): Promise<ApiResponse<T>> => {
    let response: Response;
    try {
      response = await fetch(`${API_ENDPOINT}?action=${action}`, options);
    } catch (error) {
      throw new Error('Не удалось подключиться к серверу.');
    }

    let payload: ApiResponse<T>;
    try {
      payload = (await response.json()) as ApiResponse<T>;
    } catch (err) {
      throw new Error('Сервер вернул некорректный ответ.');
    }

    if (response.status === 401) {
      setAuthState(null);
      throw new Error(payload?.error ?? 'Требуется авторизация.');
    }

    if (!response.ok || payload.success === false) {
      throw new Error(payload?.error ?? 'Не удалось выполнить запрос.');
    }

    return payload;
  };

  const openPreview = (): void => {
    if (!state.photos.length) {
      notify('Добавьте хотя бы одну фотографию для предпросмотра.', 'error');
      return;
    }

    if (!previewGrid) return;
    previewGrid.textContent = '';
    state.photos.forEach((photo) => {
      const img = document.createElement('img');
      img.src = photo.file_path;
      img.alt = 'Предпросмотр изображения';
      // Предотвращаем выделение и перетаскивание изображения
      img.draggable = false;
      img.style.userSelect = 'none';
      img.style.webkitUserSelect = 'none';
      img.addEventListener('dragstart', (e) => e.preventDefault());
      img.addEventListener('contextmenu', (e) => e.preventDefault());
      previewGrid.appendChild(img);
    });

    if (previewModal) {
      previewModal.setAttribute('aria-hidden', 'false');
    }
  };

  const closePreview = (): void => {
    if (previewModal) {
      previewModal.setAttribute('aria-hidden', 'true');
    }
  };


  const setAuthState = (user: User | null): void => {
    state.user = user;
    updateAuthVisibility();
    if (!user) {
      state.photos = [];
      renderGrid();
    }
  };

  const headerLoginBtn = document.getElementById('header-login-btn') as HTMLButtonElement;

  const updateAuthVisibility = (): void => {
    const isAuth = Boolean(state.user);
    if (plannerArea) {
      plannerArea.hidden = !isAuth;
    }
    if (authPanel) {
      // Показываем панель авторизации, если пользователь не авторизован
      authPanel.hidden = isAuth;
      if (isAuth) {
        authPanel.setAttribute('aria-hidden', 'true');
        // Убираем фокус с элементов внутри, если они в фокусе
        const focusedElement = authPanel.querySelector(':focus');
        if (focusedElement && focusedElement instanceof HTMLElement) {
          focusedElement.blur();
        }
      } else {
        authPanel.removeAttribute('aria-hidden');
      }
    }
    if (headerLoginBtn) {
      // Показываем кнопку входа в header, если пользователь не авторизован
      headerLoginBtn.hidden = isAuth;
    }
    if (bottomNav) {
      bottomNav.hidden = !isAuth;
    }
    if (uploadFab && uploadFab.parentElement) {
      // FAB показываем только если пользователь авторизован И bottom-nav скрыт (на десктопе)
      uploadFab.parentElement.style.display = (isAuth && bottomNav?.hidden) ? 'block' : 'none';
    }
    if (logoutBtn) {
      logoutBtn.disabled = !isAuth;
      logoutBtn.hidden = !isAuth;
    }
    if (changePasswordBtn) {
      changePasswordBtn.disabled = !isAuth;
      changePasswordBtn.hidden = !isAuth;
    }
    if (userDisplay) {
      userDisplay.textContent = isAuth ? (state.user?.username || '—') : '—';
    }
    if (userIdDisplay) {
      userIdDisplay.textContent = isAuth ? `ID: ${state.user?.id || ''}` : '';
    }
  };

  const scrollToAuth = (): void => {
    if (authPanel) {
      authPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Фокусируемся на первом поле ввода
      const firstInput = authPanel.querySelector('input') as HTMLInputElement;
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 300);
      }
    }
  };

  const openMenu = (): void => {
    if (menuPanel) {
      menuPanel.setAttribute('aria-hidden', 'false');
    }
  };

  const closeMenu = (): void => {
    if (menuPanel) {
      menuPanel.setAttribute('aria-hidden', 'true');
    }
  };

  const switchAuthView = (mode: 'login' | 'register'): void => {
    if (!loginForm || !registerForm) return;
    const showRegister = mode === 'register';
    loginForm.hidden = showRegister;
    registerForm.hidden = !showRegister;
    if (authTabLogin) authTabLogin.classList.toggle('active', !showRegister);
    if (authTabRegister) authTabRegister.classList.toggle('active', showRegister);
  };

  const refreshAuthState = async (): Promise<void> => {
    try {
      const response = await apiRequest<User>('auth_status');
      if (response.authenticated && response.user) {
        setAuthState(response.user);
        await loadPhotos();
      } else {
        setAuthState(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleAuthSubmit = async (event: Event, mode: 'login' | 'register'): Promise<void> => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const payload: AuthPayload = {
      username: String(formData.get('username') || '').trim(),
      password: String(formData.get('password') || '').trim(),
    };

    if (!payload.username || !payload.password) {
      notify('Заполните логин и пароль.', 'error');
      return;
    }

    try {
      const response = await apiRequest<User>(mode, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.user) {
        setAuthState(response.user);
        await loadPhotos();
        notify(mode === 'register' ? 'Аккаунт создан, добро пожаловать!' : 'Вы успешно вошли.');
      }
    } catch (error) {
      notify((error as Error).message, 'error');
    }
  };

  const closeAllModals = (): void => {
    // Закрываем меню
    if (menuPanel) {
      menuPanel.setAttribute('aria-hidden', 'true');
    }
    // Закрываем все модальные окна
    if (previewModal) {
      previewModal.setAttribute('aria-hidden', 'true');
    }
    if (postSelectModal) {
      postSelectModal.setAttribute('aria-hidden', 'true');
    }
    if (postPreviewModal) {
      postPreviewModal.setAttribute('aria-hidden', 'true');
    }
    if (changePasswordModal) {
      changePasswordModal.setAttribute('aria-hidden', 'true');
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await apiRequest('logout', { method: 'POST' });
      notify('Вы вышли из аккаунта.');
    } catch (error) {
      console.error(error);
    } finally {
      // Закрываем все модальные окна перед выходом
      closeAllModals();
      setAuthState(null);
      switchAuthView('login');
      if (loginForm) loginForm.reset();
      if (registerForm) registerForm.reset();
    }
  };

  const preprocessFiles = async (files: File[]): Promise<File[]> => {
    const processed: File[] = [];
    for (const file of files) {
      if (!file.type?.startsWith('image/')) {
        processed.push(file);
        continue;
      }

      try {
        const resized = await resizeImageFile(file);
        processed.push(resized);
      } catch (error) {
        console.warn('Resize failed, using original file', error);
        processed.push(file);
      }
    }
    return processed;
  };

  const resizeImageFile = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const { width, height } = image;
        const largestSide = Math.max(width, height);

        if (largestSide <= MAX_IMAGE_DIMENSION && file.size < 2 * 1024 * 1024) {
          URL.revokeObjectURL(image.src);
          resolve(file);
          return;
        }

        const scale = Math.min(MAX_IMAGE_DIMENSION / largestSide, 1);
        const targetWidth = Math.round(width * scale);
        const targetHeight = Math.round(height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(image.src);
          reject(new Error('Failed to get canvas context'));
          return;
        }
        ctx.drawImage(image, 0, 0, targetWidth, targetHeight);

        const isPng = file.type === 'image/png';
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(image.src);
            if (!blob) {
              resolve(file);
              return;
            }
            const optimized = new File([blob], file.name, {
              type: isPng ? 'image/png' : 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(optimized);
          },
          isPng ? 'image/png' : 'image/jpeg',
          isPng ? 0.92 : TARGET_JPEG_QUALITY
        );
      };
      image.onerror = (error) => {
        URL.revokeObjectURL(image.src);
        reject(error);
      };
      image.src = URL.createObjectURL(file);
    });
  };

  const openPostSelect = (): void => {
    if (!state.user) {
      notify('Авторизуйтесь, чтобы создавать посты.', 'error');
      return;
    }

    if (!state.photos.length) {
      notify('Добавьте хотя бы одну фотографию для создания поста.', 'error');
      return;
    }

    selectedPostPhotos = [];
    renderPostSelectGrid();
    updatePostSelectCounter();
    if (postSelectModal) {
      postSelectModal.setAttribute('aria-hidden', 'false');
    }
  };

  const closePostSelect = (): void => {
    if (postSelectModal) {
      postSelectModal.setAttribute('aria-hidden', 'true');
    }
    selectedPostPhotos = [];
  };

  const renderPostSelectGrid = (): void => {
    if (!postSelectGrid) return;
    postSelectGrid.textContent = '';

    state.photos.forEach((photo) => {
      const item = document.createElement('div');
      item.className = 'post-select-item';
      item.dataset.id = String(photo.id);
      if (selectedPostPhotos.some((p) => p.id === photo.id)) {
        item.classList.add('selected');
      }

      const img = document.createElement('img');
      img.src = photo.file_path;
      img.alt = 'Фото для поста';
      img.loading = 'lazy';
      // Предотвращаем выделение и перетаскивание изображения
      img.draggable = false;
      img.style.userSelect = 'none';
      img.style.webkitUserSelect = 'none';
      img.addEventListener('dragstart', (e) => e.preventDefault());
      img.addEventListener('contextmenu', (e) => e.preventDefault());
      img.addEventListener('selectstart', (e) => e.preventDefault());

      item.appendChild(img);
      item.addEventListener('click', () => togglePhotoSelection(photo, item));
      postSelectGrid.appendChild(item);
    });
  };

  const togglePhotoSelection = (photo: Photo, element: HTMLElement): void => {
    const index = selectedPostPhotos.findIndex((p) => p.id === photo.id);

    if (index >= 0) {
      selectedPostPhotos.splice(index, 1);
      element.classList.remove('selected');
    } else {
      if (selectedPostPhotos.length >= MAX_PHOTOS) {
        notify(`Можно выбрать максимум ${MAX_PHOTOS} фотографий для поста.`, 'error');
        return;
      }
      selectedPostPhotos.push(photo);
      element.classList.add('selected');
    }

    updatePostSelectCounter();
    updatePreviewPostButton();
  };

  const updatePostSelectCounter = (): void => {
    if (postSelectCounter) {
      postSelectCounter.textContent = `${selectedPostPhotos.length} / ${MAX_PHOTOS}`;
    }
  };

  const updatePreviewPostButton = (): void => {
    if (previewPostBtn) {
      previewPostBtn.disabled = selectedPostPhotos.length === 0;
    }
  };

  const openPostPreview = (): void => {
    if (selectedPostPhotos.length === 0) {
      notify('Выберите хотя бы одну фотографию для предпросмотра.', 'error');
      return;
    }

    if (!postPreviewGrid) return;
    postPreviewGrid.textContent = '';

    selectedPostPhotos.forEach((photo) => {
      const img = document.createElement('img');
      img.src = photo.file_path;
      img.alt = 'Предпросмотр фото поста';
      img.loading = 'lazy';
      // Предотвращаем выделение и перетаскивание изображения
      img.draggable = false;
      img.style.userSelect = 'none';
      img.style.webkitUserSelect = 'none';
      img.addEventListener('dragstart', (e) => e.preventDefault());
      img.addEventListener('contextmenu', (e) => e.preventDefault());
      postPreviewGrid.appendChild(img);
    });

    if (postPreviewModal) {
      postPreviewModal.setAttribute('aria-hidden', 'false');
    }
  };

  const closePostPreview = (): void => {
    if (postPreviewModal) {
      postPreviewModal.setAttribute('aria-hidden', 'true');
    }
  };

  const openChangePassword = (): void => {
    if (!state.user) {
      notify('Авторизуйтесь, чтобы изменить пароль.', 'error');
      return;
    }
    if (changePasswordForm) changePasswordForm.reset();
    if (changePasswordModal) {
      changePasswordModal.setAttribute('aria-hidden', 'false');
    }
  };

  const closeChangePassword = (): void => {
    if (changePasswordModal) {
      changePasswordModal.setAttribute('aria-hidden', 'true');
    }
    if (changePasswordForm) changePasswordForm.reset();
  };

  const handleChangePasswordSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    if (!state.user) {
      notify('Авторизуйтесь, чтобы изменить пароль.', 'error');
      return;
    }

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const payload: ChangePasswordPayload = {
      current_password: String(formData.get('current_password') || '').trim(),
      new_password: String(formData.get('new_password') || '').trim(),
    };

    if (!payload.current_password || !payload.new_password) {
      notify('Заполните все поля.', 'error');
      return;
    }

    if (payload.new_password.length < 6) {
      notify('Новый пароль должен содержать минимум 6 символов.', 'error');
      return;
    }

    try {
      await apiRequest('change_password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      notify('Пароль успешно изменён.');
      closeChangePassword();
    } catch (error) {
      notify((error as Error).message, 'error');
    }
  };

  // Event Listeners
  if (previewModal) {
    previewModal.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target === previewModal || target.classList.contains('modal__backdrop')) {
        closePreview();
      }
    });
  }

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (previewModal?.getAttribute('aria-hidden') === 'false') {
        closePreview();
      }
      if (postSelectModal?.getAttribute('aria-hidden') === 'false') {
        closePostSelect();
      }
      if (postPreviewModal?.getAttribute('aria-hidden') === 'false') {
        closePostPreview();
      }
      if (changePasswordModal?.getAttribute('aria-hidden') === 'false') {
        closeChangePassword();
      }
      if (menuPanel?.getAttribute('aria-hidden') === 'false') {
        closeMenu();
      }
    }
  });

  if (postSelectModal) {
    postSelectModal.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target === postSelectModal || target.classList.contains('modal__backdrop')) {
        closePostSelect();
      }
    });
  }

  if (postPreviewModal) {
    postPreviewModal.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target === postPreviewModal || target.classList.contains('modal__backdrop')) {
        closePostPreview();
      }
    });
  }

  if (changePasswordModal) {
    changePasswordModal.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target === changePasswordModal || target.classList.contains('modal__backdrop')) {
        closeChangePassword();
      }
    });
  }

  if (authTabLogin) authTabLogin.addEventListener('click', () => switchAuthView('login'));
  if (authTabRegister) authTabRegister.addEventListener('click', () => switchAuthView('register'));
  if (loginForm) loginForm.addEventListener('submit', (event) => handleAuthSubmit(event, 'login'));
  if (registerForm) registerForm.addEventListener('submit', (event) => handleAuthSubmit(event, 'register'));
  if (logoutBtn) logoutBtn.addEventListener('click', handleLogout);

  const handleUploadClick = (): void => {
    if (fileInput) fileInput.click();
  };

  if (uploadBtn) uploadBtn.addEventListener('click', handleUploadClick);
  if (uploadFab) uploadFab.addEventListener('click', handleUploadClick);
  if (fileInput) fileInput.addEventListener('change', handleFileUpload);
  if (resetBtn) resetBtn.addEventListener('click', handleReset);
  if (previewBtn) previewBtn.addEventListener('click', openPreview);
  if (closePreviewBtn) closePreviewBtn.addEventListener('click', closePreview);
  if (createPostBtn) createPostBtn.addEventListener('click', openPostSelect);
  if (closePostSelectBtn) closePostSelectBtn.addEventListener('click', closePostSelect);
  if (cancelPostBtn) cancelPostBtn.addEventListener('click', closePostSelect);
  if (previewPostBtn) previewPostBtn.addEventListener('click', openPostPreview);
  if (closePostPreviewBtn) closePostPreviewBtn.addEventListener('click', closePostPreview);
  if (changePasswordBtn) changePasswordBtn.addEventListener('click', openChangePassword);
  if (closeChangePasswordBtn) closeChangePasswordBtn.addEventListener('click', closeChangePassword);
  if (cancelChangePasswordBtn) cancelChangePasswordBtn.addEventListener('click', closeChangePassword);
  if (changePasswordForm) changePasswordForm.addEventListener('submit', handleChangePasswordSubmit);

  if (tabFeed) tabFeed.addEventListener('click', () => switchSection('feed'));
  if (tabDrafts) tabDrafts.addEventListener('click', () => switchSection('draft'));

  if (menuBtn) menuBtn.addEventListener('click', openMenu);
  if (closeMenuBtn) closeMenuBtn.addEventListener('click', closeMenu);
  if (headerLoginBtn) headerLoginBtn.addEventListener('click', scrollToAuth);

  if (menuPanel) {
    menuPanel.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target === menuPanel || target.classList.contains('menu-panel__backdrop')) {
        closeMenu();
      }
    });
  }

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (menuPanel?.getAttribute('aria-hidden') === 'false') {
        closeMenu();
      }
    }
  });

  // ============================================
  // Theme Management
  // ============================================
  
  const THEME_STORAGE_KEY = 'grid-planner-theme';
  type Theme = 'light' | 'dark' | 'auto';
  
  const getStoredTheme = (): Theme => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      return stored;
    }
    return 'auto';
  };
  
  const setStoredTheme = (theme: Theme): void => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  };
  
  const getEffectiveTheme = (): 'light' | 'dark' => {
    const theme = getStoredTheme();
    if (theme === 'auto') {
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return theme;
  };
  
  const applyTheme = (theme: Theme): void => {
    const root = document.documentElement;
    root.removeAttribute('data-theme');
    
    if (theme === 'auto') {
      root.removeAttribute('data-theme');
      // Используем медиа-запрос
      const effectiveTheme = getEffectiveTheme();
      updateThemeUI(effectiveTheme, 'auto');
    } else {
      root.setAttribute('data-theme', theme);
      updateThemeUI(theme, theme);
    }
    
    setStoredTheme(theme);
  };
  
  const updateThemeUI = (effectiveTheme: 'light' | 'dark', storedTheme: Theme): void => {
    const iconEl = document.getElementById('theme-toggle-icon');
    if (iconEl && themeToggleText) {
      if (storedTheme === 'light') {
        setIcon(iconEl, 'sun');
        themeToggleText.textContent = 'Светлая тема';
      } else if (storedTheme === 'dark') {
        setIcon(iconEl, 'moon');
        themeToggleText.textContent = 'Тёмная тема';
      } else {
        setIcon(iconEl, effectiveTheme === 'light' ? 'sun' : 'moon');
        themeToggleText.textContent = 'Автоматическая тема';
      }
    }
  };
  
  const toggleTheme = (): void => {
    const currentTheme = getStoredTheme();
    let nextTheme: Theme;
    
    if (currentTheme === 'auto') {
      nextTheme = 'dark';
    } else if (currentTheme === 'dark') {
      nextTheme = 'light';
    } else {
      nextTheme = 'auto';
    }
    
    applyTheme(nextTheme);
  };
  
  // Слушаем изменения системной темы для автоматического режима
  window.matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    if (getStoredTheme() === 'auto') {
      applyTheme('auto');
    }
  });
  
  // Инициализация темы при загрузке
  const initTheme = (): void => {
    const theme = getStoredTheme();
    applyTheme(theme);
  };
  
  // Скрываем кнопки удаления при клике вне элементов
  document.addEventListener('pointerdown', (event: PointerEvent) => {
    const target = event.target as HTMLElement;
    // Если клик не на элементе сетки и не на кнопке удаления, скрываем все кнопки
    if (!target.closest('.grid-item') && !target.closest('.grid-item__delete')) {
      const allItems = grid?.querySelectorAll('.grid-item.show-delete');
      allItems?.forEach(item => item.classList.remove('show-delete'));
    }
  });
  
  // Инициализация видимости перед проверкой авторизации
  updateAuthVisibility();
  
  // Инициализация темы
  initTheme();
  
  // Обработчик переключения темы
  if (themeToggleBtn) {
    themeToggleBtn.addEventListener('click', toggleTheme);
  }
  
  // ============================================
  // PWA Install Prompt
  // ============================================
  
  let deferredPrompt: any = null;
  const INSTALL_PROMPT_DISMISSED_KEY = 'grid-planner-install-dismissed';
  const INSTALL_PROMPT_DELAY = 3000; // 3 seconds
  
  // Listen for beforeinstallprompt event
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    
    // Check if user previously dismissed the prompt
    const dismissed = localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
    
    // Show prompt if not dismissed or dismissed more than 7 days ago
    if (!dismissed || daysSinceDismissed > 7) {
      // Show install prompt after delay
      setTimeout(() => {
        if (installPrompt) {
          installPrompt.hidden = false;
        }
      }, INSTALL_PROMPT_DELAY);
    }
  });
  
  // Handle install accept
  if (installAcceptBtn) {
    installAcceptBtn.addEventListener('click', async () => {
      if (!deferredPrompt) return;
      
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      
      // Hide the install prompt
      if (installPrompt) {
        installPrompt.hidden = true;
      }
      
      if (outcome === 'accepted') {
        notify('Приложение устанавливается...');
      }
      
      // Clear the deferredPrompt
      deferredPrompt = null;
    });
  }
  
  // Handle install dismiss
  if (installDismissBtn) {
    installDismissBtn.addEventListener('click', () => {
      // Hide the install prompt
      if (installPrompt) {
        installPrompt.hidden = true;
      }
      
      // Save dismissal timestamp
      localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, String(Date.now()));
    });
  }
  
  // Listen for app installed event
  window.addEventListener('appinstalled', () => {
    // Hide the install prompt
    if (installPrompt) {
      installPrompt.hidden = true;
    }
    
    notify('Приложение успешно установлено! 🎉');
    deferredPrompt = null;
  });
  
  // Затем проверяем статус авторизации
  refreshIcons();
  refreshAuthState();
});
